// SNIGJ - Border Control Page

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { borderAPI } from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, 
  Search, 
  Filter,
  AlertTriangle,
  Shield,
  MapPin,
  Clock,
  User,
  Plane
} from 'lucide-react';
import { toast } from 'sonner';

const BorderControlPage = () => {
  const { language, user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filters, setFilters] = useState({
    alert_type: '',
    status: ''
  });
  const [formData, setFormData] = useState({
    alert_type: 'WANTED',
    person_name: '',
    unji: '',
    description: '',
    severity: 'HIGH',
    status: 'ACTIVE'
  });

  useEffect(() => {
    loadAlerts();
  }, [filters]);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const params = { ...filters, limit: 100 };
      Object.keys(params).forEach(key => !params[key] && delete params[key]);
      const response = await borderAPI.list(params);
      setAlerts(response.data);
    } catch (error) {
      console.error('Failed to load alerts:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAlert = async (e) => {
    e.preventDefault();
    try {
      await borderAPI.create(formData);
      toast.success(language === 'fr' ? 'Alerte créée' : 'Alert created');
      setShowCreateModal(false);
      loadAlerts();
      setFormData({
        alert_type: 'WANTED',
        person_name: '',
        unji: '',
        description: '',
        severity: 'HIGH',
        status: 'ACTIVE'
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de création' : 'Failed to create alert'));
    }
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      HIGH: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800',
      MEDIUM: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800',
      LOW: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800'
    };
    return `px-2 py-1 rounded text-xs font-medium border ${styles[severity] || styles.MEDIUM}`;
  };

  const getAlertTypeIcon = (type) => {
    switch (type) {
      case 'WANTED': return AlertTriangle;
      case 'TRAVEL_BAN': return Plane;
      case 'FUGITIVE': return Shield;
      default: return AlertTriangle;
    }
  };

  const alertTypes = ['WANTED', 'TRAVEL_BAN', 'FUGITIVE'];
  const severities = ['HIGH', 'MEDIUM', 'LOW'];
  const statuses = ['ACTIVE', 'RESOLVED', 'EXPIRED'];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="border-control-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">
              {t(language, 'border.title')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' ? 'Gestion des alertes et recherches' : 'Alert and search management'}
            </p>
          </div>
          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-red-600 hover:bg-red-700" data-testid="new-alert-btn">
                <AlertTriangle className="w-4 h-4" />
                {t(language, 'border.newAlert')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading">{t(language, 'border.newAlert')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateAlert} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t(language, 'border.alertType')}</Label>
                    <Select
                      value={formData.alert_type}
                      onValueChange={(value) => setFormData({ ...formData, alert_type: value })}
                    >
                      <SelectTrigger data-testid="alert-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {alertTypes.map((type) => (
                          <SelectItem key={type} value={type}>
                            {t(language, `border.${type.toLowerCase().replace('_', '')}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t(language, 'border.severity')}</Label>
                    <Select
                      value={formData.severity}
                      onValueChange={(value) => setFormData({ ...formData, severity: value })}
                    >
                      <SelectTrigger data-testid="alert-severity-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {severities.map((s) => (
                          <SelectItem key={s} value={s}>
                            {t(language, `border.${s.toLowerCase()}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t(language, 'border.personName')}</Label>
                  <Input
                    value={formData.person_name}
                    onChange={(e) => setFormData({ ...formData, person_name: e.target.value })}
                    required
                    data-testid="alert-person-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t(language, 'prisoners.unji')} (optionnel)</Label>
                  <Input
                    value={formData.unji}
                    onChange={(e) => setFormData({ ...formData, unji: e.target.value })}
                    placeholder="UNJI-XXXX"
                    className="font-mono"
                    data-testid="alert-unji"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                    rows={3}
                    data-testid="alert-description"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                    {t(language, 'common.cancel')}
                  </Button>
                  <Button type="submit" className="bg-red-600 hover:bg-red-700 gap-2" data-testid="create-alert-submit">
                    <AlertTriangle className="w-4 h-4" />
                    {language === 'fr' ? 'Créer l\'alerte' : 'Create Alert'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Active Alerts Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded bg-red-100 dark:bg-red-900/50 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading">{alerts.filter(a => a.alert_type === 'WANTED' && a.status === 'ACTIVE').length}</p>
                <p className="text-sm text-muted-foreground">{t(language, 'border.wanted')}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded bg-yellow-100 dark:bg-yellow-900/50 flex items-center justify-center">
                <Plane className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading">{alerts.filter(a => a.alert_type === 'TRAVEL_BAN' && a.status === 'ACTIVE').length}</p>
                <p className="text-sm text-muted-foreground">{t(language, 'border.travelBan')}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading">{alerts.filter(a => a.alert_type === 'FUGITIVE' && a.status === 'ACTIVE').length}</p>
                <p className="text-sm text-muted-foreground">{t(language, 'border.fugitive')}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={t(language, 'common.search') + '...'}
                    className="pl-10"
                    data-testid="alerts-search"
                  />
                </div>
              </div>
              <div className="w-[150px]">
                <Select
                  value={filters.alert_type || "ALL"}
                  onValueChange={(value) => setFilters({ ...filters, alert_type: value === "ALL" ? "" : value })}
                >
                  <SelectTrigger data-testid="filter-alert-type">
                    <SelectValue placeholder={t(language, 'border.alertType')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                    {alertTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {t(language, `border.${type.toLowerCase().replace('_', '')}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[150px]">
                <Select
                  value={filters.status || "ALL"}
                  onValueChange={(value) => setFilters({ ...filters, status: value === "ALL" ? "" : value })}
                >
                  <SelectTrigger data-testid="filter-alert-status">
                    <SelectValue placeholder={t(language, 'cases.status')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                    {statuses.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={loadAlerts} className="gap-2">
                <Filter className="w-4 h-4" />
                {t(language, 'common.filter')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Alerts List */}
        <div className="space-y-4">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 skeleton rounded" />
                    <div className="flex-1 space-y-2">
                      <div className="h-5 skeleton w-1/3" />
                      <div className="h-4 skeleton w-2/3" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : alerts.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                {t(language, 'common.noData')}
              </CardContent>
            </Card>
          ) : (
            alerts.map((alert) => {
              const AlertIcon = getAlertTypeIcon(alert.alert_type);
              return (
                <Card 
                  key={alert.alert_id} 
                  className={`${alert.status === 'ACTIVE' ? 'border-l-4 border-l-red-500' : ''}`}
                  data-testid={`alert-card-${alert.alert_id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded flex items-center justify-center ${
                        alert.alert_type === 'WANTED' ? 'bg-red-100 dark:bg-red-900/30' :
                        alert.alert_type === 'TRAVEL_BAN' ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                        'bg-purple-100 dark:bg-purple-900/30'
                      }`}>
                        <AlertIcon className={`w-6 h-6 ${
                          alert.alert_type === 'WANTED' ? 'text-red-600' :
                          alert.alert_type === 'TRAVEL_BAN' ? 'text-yellow-600' :
                          'text-purple-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-heading font-bold text-lg">{alert.person_name}</h3>
                          <span className={getSeverityBadge(alert.severity)}>
                            {t(language, `border.${alert.severity.toLowerCase()}`)}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            alert.status === 'ACTIVE' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                            'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                          }`}>
                            {alert.status}
                          </span>
                        </div>
                        <p className="text-muted-foreground mb-3">{alert.description}</p>
                        <div className="flex items-center gap-6 text-sm text-muted-foreground">
                          {alert.unji && (
                            <span className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              <span className="font-mono">{alert.unji}</span>
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {new Date(alert.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                          </span>
                          {alert.last_seen && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {alert.last_seen.location}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BorderControlPage;
